import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  Check,
  MessageCircle,
  PlayCircle,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { trackCRMEvent } from "../lib/crmTracking";

const LANDING_VIDEO_URL = "https://www.youtube.com/embed/QrPr1f1HShE";
const SHOW_VIDEO_SECTION = true;

const heroBullets = [
  "Você define o preço",
  "Você atende seus clientes",
  "Você fica com o lucro",
];

const opportunityItems = [
  "Renda recorrente todos os meses",
  "Sem limite de crescimento",
  "Alta demanda no mercado",
];

const gatewayDifferentials = [
  "Você e dono da sua carteira de clientes",
  "Sem concorrência direta da fábrica",
  "Suporte técnico especializado",
  "Treinamento completo",
  "Sistema validado no mercado",
];

const simplicityItems = [
  "Sistema pronto",
  "Treinamento e suporte",
  "Materiais comerciais",
  "Plataforma de gestão de clientes",
];

const setMetaDescription = (content) => {
  if (typeof document === "undefined") {
    return;
  }

  let meta = document.querySelector('meta[name="description"]');

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
};

const LpCTA = () => {
  const navigate = useNavigate();
  const [videoStarted, setVideoStarted] = useState(false);

  const embeddedVideoUrl = useMemo(() => {
    if (!LANDING_VIDEO_URL) {
      return "";
    }

    try {
      const url = new URL(LANDING_VIDEO_URL);

      if (!url.searchParams.has("autoplay")) {
        url.searchParams.set("autoplay", "1");
      }

      if (!url.searchParams.has("rel")) {
        url.searchParams.set("rel", "0");
      }

      return url.toString();
    } catch (error) {
      return LANDING_VIDEO_URL;
    }
  }, []);

  useEffect(() => {
    document.title = "Revenda de Software | Gway Sistemas";
    setMetaDescription(
      "Ganhe dinheiro todos os meses revendendo software com a Gway Sistemas. Fale com nossa equipe e comece como revendedor."
    );

    trackCRMEvent({
      eventType: "page_view",
      eventName: "lpcta_view",
      metadata: {
        page: "lpcta",
      },
      dedupeKey: "page:lpcta",
      dedupeWindowMs: 2000,
    });
  }, []);

  const handleContactNavigation = (origin) => {
    trackCRMEvent({
      eventType: "cta_click",
      eventName: "lpcta_contact_click",
      metadata: {
        page: "lpcta",
        origin,
      },
    });

    navigate("/contato");
  };

  const handleScheduleNavigation = (origin = "secondary_cta") => {
    trackCRMEvent({
      eventType: "cta_click",
      eventName: "lpcta_schedule_click",
      metadata: {
        page: "lpcta",
        origin,
      },
    });

    navigate("/contato#agenda");
  };

  const handleVideoPlay = () => {
    if (videoStarted) {
      return;
    }

    setVideoStarted(true);
    trackCRMEvent({
      eventType: "video_interaction",
      eventName: "lpcta_video_play",
      metadata: {
        page: "lpcta",
        video_url: LANDING_VIDEO_URL,
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 text-gray-900">
      <header className="border-b bg-white/90 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => handleContactNavigation("header")}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
          >
            Quero começar como revendedor
          </Button>
        </div>
      </header>

      <main>
        <section
          className={`mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:px-8 lg:py-20 ${
            SHOW_VIDEO_SECTION ? "lg:grid-cols-[1.05fr_0.95fr]" : ""
          }`}
        >
          <div className="flex flex-col justify-center">
            <span className="mb-6 inline-flex w-fit items-center rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm">
              Mais de 30 anos de mercado • Sistema completo • Suporte direto
            </span>

            <h1 className="max-w-3xl text-4xl font-black leading-tight text-gray-900 sm:text-5xl lg:text-6xl">
              Ganhe dinheiro todos os meses revendendo software
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">
              Monte sua própria carteira de clientes e gere renda recorrente
              com um sistema já validado no mercado.
            </p>

            <div className="mt-8 grid gap-3 sm:max-w-lg">
              {heroBullets.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-white px-4 py-3 shadow-sm"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <Check className="h-4 w-4" />
                  </div>
                  <p className="text-sm font-semibold text-gray-700">{item}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Button
                onClick={() => handleContactNavigation("hero_primary")}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 text-white hover:from-blue-700 hover:to-purple-700"
              >
                Quero começar como revendedor
                <ArrowRight className="h-4 w-4" />
              </Button>

              <Button
                onClick={() => handleScheduleNavigation("hero_secondary")}
                size="lg"
                variant="outline"
                className="border-blue-200 bg-white px-8 text-blue-700 hover:bg-blue-50"
              >
                Agendar apresentação
                <CalendarDays className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {SHOW_VIDEO_SECTION ? (
            <div className="flex items-center">
              <Card className="w-full overflow-hidden border-0 bg-white shadow-xl">
                <CardContent className="p-5 sm:p-6">
                  <div className="mb-5 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">
                        Veja como funciona na pratica
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Assista e entenda como você pode começar a gerar renda recorrente com a revenda de software.
                      </p>
                    </div>
                    <PlayCircle className="h-8 w-8 text-purple-600" />
                  </div>

                  {LANDING_VIDEO_URL ? (
                    videoStarted ? (
                      <div className="aspect-video overflow-hidden rounded-3xl bg-gray-900">
                        <iframe
                          src={embeddedVideoUrl}
                          title="Video sobre revenda de software"
                          className="h-full w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleVideoPlay}
                        className="flex aspect-video w-full flex-col items-center justify-center rounded-3xl bg-gradient-to-br from-blue-600 to-purple-600 px-6 text-center text-white transition-transform hover:scale-[1.01]"
                      >
                        <PlayCircle className="h-16 w-16 text-yellow-300" />
                        <p className="mt-4 text-2xl font-bold">
                          Clique para assistir
                        </p>
                        <p className="mt-2 max-w-md text-sm leading-6 text-blue-100">
                          Veja como a revenda funciona na pratica e por que essa
                          oportunidade gera receita mensal.
                        </p>
                      </button>
                    )
                  ) : (
                    <div className="flex aspect-video flex-col items-center justify-center rounded-3xl border border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 px-6 text-center">
                      <PlayCircle className="h-16 w-16 text-purple-600" />
                      <p className="mt-4 text-xl font-bold text-gray-900">
                        Area reservada para o seu video
                      </p>
                      <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
                        Basta informar a URL de embed em <code>LANDING_VIDEO_URL</code> para
                        publicar o conteudo.
                      </p>
                    </div>
                  )}

                  <div className="mt-6 rounded-3xl bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-5 text-white">
                    <p className="text-sm uppercase tracking-[0.2em] text-yellow-300">
                      Oportunidade real
                    </p>
                    <p className="mt-2 text-lg font-semibold">
                      Sistema validado, suporte direto e espaco para você construir sua própria carteira.
                    </p>
                    <Button
                      onClick={() => handleContactNavigation("video_section")}
                      className="mt-4 w-full bg-yellow-500 text-black hover:bg-yellow-600"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Falar com a equipe
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </section>

        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-0 bg-white shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-gray-900">
                  Uma nova fonte de renda para você
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base leading-7 text-gray-600">
                  Com poucos clientes você já começa a gerar ganhos mensais.
                  Com mais clientes, você constrói um negócio escalável.
                </p>

                <div className="mt-6 grid gap-3">
                  {opportunityItems.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-3"
                    >
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      <p className="text-sm font-semibold text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>

                <p className="mt-6 text-lg font-bold text-blue-700">
                  Transforme vendas em receita mensal.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-gray-900">
                  Por que escolher a Gateway?
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3">
                {gatewayDifferentials.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-white px-4 py-3"
                  >
                    <ShieldCheck className="h-5 w-5 text-blue-600" />
                    <p className="text-sm font-semibold text-gray-700">{item}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Card className="border-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-xl">
            <CardContent className="grid gap-8 p-8 lg:grid-cols-[0.9fr_1.1fr] lg:p-10">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-yellow-300">
                  Você não precisa começar do zero
                </p>
                <h2 className="mt-3 text-3xl font-black">
                  A Gateway fornece toda a estrutura para você começar rapidamente.
                </h2>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {simplicityItems.map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl bg-white/10 px-4 py-4 text-sm font-semibold text-white backdrop-blur"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-0 bg-white shadow-xl">
              <CardContent className="p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">
                  Prefere uma apresentação?
                </p>
                <h3 className="mt-3 text-3xl font-black text-gray-900">
                  Agende um horário e veja como funciona na prática.
                </h3>
                <p className="mt-3 text-base leading-7 text-gray-600">
                  Se fizer mais sentido para você, fale com a equipe em uma apresentação guiada.
                </p>
                <Button
                  onClick={() => handleScheduleNavigation("schedule_block")}
                  className="mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                >
                  Agendar apresentação
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 bg-gray-900 shadow-xl">
              <CardContent className="p-8 text-white">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-yellow-300">
                  Comece hoje mesmo
                </p>
                <h3 className="mt-3 text-3xl font-black">
                  Sem investimento inicial. Sem limite de crescimento.
                </h3>
                <p className="mt-3 text-base leading-7 text-gray-300">
                  Entre agora no processo comercial e descubra como construir sua própria carteira de clientes.
                </p>
                <Button
                  onClick={() => handleContactNavigation("final_cta")}
                  className="mt-6 bg-yellow-500 text-black hover:bg-yellow-600"
                >
                  Quero ser revendedor
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LpCTA;
